import { useCallback, useMemo } from 'react';

import { useNotices } from '@/features/notices/useNotices';
import { useAuthStore } from '@/shared/store/auth.store';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { Account } from '../generated/Account';
import { Auth } from '../generated/Auth';
import { Currency } from '../generated/Currency';
import { Data } from '../generated/Data';
import { FileStorage } from '../generated/FileStorage';
import { GtwAuth } from '../generated/GtwAuth';
import { GtwCase } from '../generated/GtwCase';
import { GtwCertificate } from '../generated/GtwCertificate';
import { GtwChat } from '../generated/GtwChat';
import { GtwDictionary } from '../generated/GtwDictionary';
import { GtwLlmSocket } from '../generated/GtwLlmSocket';
import { GtwScenario } from '../generated/GtwScenario';
import { ApiConfig } from '../generated/http-client';
import { HubSettings } from '../generated/HubSettings';
import { MailServer } from '../generated/MailServer';
import { Profile } from '../generated/Profile';
import { RegionName } from '../generated/RegionName';
import { Scenario } from '../generated/Scenario';
import { Subscription } from '../generated/Subscription';
import { Synchronizer } from '../generated/Synchronizer';
import { Wills } from '../generated/Wills';
import { getApiConfig } from '../helpers/getApiConfig';
import { getBearerToken } from '../utils';

import { useExpiredNewAuthTokenHandle } from './useExpiredNewAuthTokenHandle';
import { useLogout } from './useLogout';

const apiNsMap = {
  account: (config: ApiConfig) => new Account(config),
  auth: (config: ApiConfig) => new Auth(config),
  myhubRegions: (config: ApiConfig) => new RegionName(config),
  profiles: (config: ApiConfig) => new Profile(config),
  synchronizer: (config: ApiConfig) => new Synchronizer(config),
  scenarios: (config: ApiConfig) => new Scenario(config),
  subscriptions: (config: ApiConfig) => new Subscription(config),
  wills: (config: ApiConfig) => new Wills(config),
  mailServer: (config: ApiConfig) => new MailServer(config),
  userDataCollector: (config: ApiConfig) => new Data(config),

  /* common */
  fileStorage: (config: ApiConfig) => new FileStorage(config),
  currencies: (config: ApiConfig) => new Currency(config),
  hubSettings: (config: ApiConfig) => new HubSettings(config),

  /* new services */
  gtwAuth: (config: ApiConfig) => new GtwAuth(config),
  gtwDictionary: (config: ApiConfig) => new GtwDictionary(config),
  gtwScenarios: (config: ApiConfig) => new GtwScenario(config),
  gtwCertificates: (config: ApiConfig) => new GtwCertificate(config),
  gtwLlmSocket: (config: ApiConfig) => new GtwLlmSocket(config),
  gtwCase: (config: ApiConfig) => new GtwCase(config),
  gtwChat: (config: ApiConfig) => new GtwChat(config),
};

type ApiNsMap = typeof apiNsMap;
const tenantNamespaces: (keyof ApiNsMap)[] = ['gtwCase', 'gtwChat'];

export const useApiBase = () => {
  const { t } = useTranslation('common');

  const { authAccessToken, tenantOwners } = useAuthStore((s) => ({
    authAccessToken: s.accessToken,
    tenantOwners: s.tenantOwners,
  }));
  const notices = useNotices();
  const { logout } = useLogout();

  const onLogout = useCallback(async () => {
    await logout();
    notices.error(t('sessionExpiredAlert'));
  }, [logout]);

  const { onNewAuthTokenExpired } = useExpiredNewAuthTokenHandle({
    logout: onLogout,
  });

  return useMemo(() => {
    const config = getApiConfig(authAccessToken, {
      logout: onLogout,
    });

    const gtwWithTenantConfig = getApiConfig(getBearerToken(authAccessToken ?? undefined), {
      logout: onLogout,
      onTokenExpired: onNewAuthTokenExpired,
      // TODO: add support for multiple tenants
      tenantProfileId: tenantOwners?.[0]?.profileId,
    });

    const gtwConfig = getApiConfig(getBearerToken(authAccessToken ?? undefined), {
      logout: onLogout,
      onTokenExpired: onNewAuthTokenExpired,
    });

    return <K extends keyof ApiNsMap>(ns: K): ReturnType<ApiNsMap[K]> => {
      let cfg = config;

      if (ns.startsWith('gtw')) {
        cfg = tenantNamespaces.includes(ns) ? gtwWithTenantConfig : gtwConfig;
      }

      return apiNsMap[ns](cfg) as ReturnType<ApiNsMap[K]>;
    };
  }, [authAccessToken, onLogout, onNewAuthTokenExpired, tenantOwners]);
};

// # V2
// type ApiNsMap = {
//   'auth': () => Auth;
//   'account': () => Account;
//   'will': () => Will,
// };

// export const useApi = () => {
//   const token = useAuthStore((s) => s.token);

//   return useMemo(() => {
//     const config = getApiConfig(token);
//     const typeMap: ApiNsMap = {
//       auth: () => new Auth(config),
//       account: () => new Account(config),
//       will: () => new Will(config),
//     };

//     return <K extends keyof ApiNsMap>(ns: K): ApiNsMap[K] => typeMap[ns];
//   }, [token]);
// };
